/**
 * writeQueue/beforeunload.ts — browser-side tab-close protection.
 *
 * When the queue has pending or inflight mutations and the editor tries
 * to close the tab / navigate away, fire the browser's "Leave site?"
 * confirmation. The editor's work is durably stored in IndexedDB and
 * WILL be resumed on next session, but in the meantime they should know
 * they have unsynced edits before they close.
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1 enhanced.
 *
 * Status: LIVE (2026-05-19). Installed from AdminLayout.tsx's useEffect.
 */

import { listByStatus } from './db';

type Unbind = () => void;

let installed: Unbind | null = null;

/**
 * Install the beforeunload guard. Idempotent — second+ calls return the
 * existing unbinder. Returns a function that removes the guard.
 */
export function install(): Unbind {
  if (installed) return installed;

  // We can't await IndexedDB inside the beforeunload handler — the
  // browser fires it synchronously and ignores async work. Instead, we
  // cache a "has pending" flag that the sync worker (or queue mutations)
  // could update; the handler just reads the flag.
  //
  // For the scaffold, we use a simple approach: poll the queue every 2s
  // and cache the flag. Cheap, and the editor's tab-close is rare enough
  // that 2s of staleness is fine.
  let hasPending = false;

  async function refreshFlag(): Promise<void> {
    try {
      const pending = await listByStatus('pending');
      const inflight = await listByStatus('inflight');
      const failed = await listByStatus('failed_giveup');
      hasPending = pending.length > 0 || inflight.length > 0 || failed.length > 0;
    } catch {
      // IndexedDB unavailable — fail safe by not warning.
      hasPending = false;
    }
  }

  const pollInterval = setInterval(refreshFlag, 2000);
  refreshFlag(); // initial

  function handler(e: BeforeUnloadEvent): string | undefined {
    if (!hasPending) return undefined;
    // Modern browsers ignore the returned string and show a generic
    // "Leave site?" prompt. We set preventDefault + returnValue for
    // maximum cross-browser compatibility.
    e.preventDefault();
    e.returnValue = 'You have unsynced editorial changes. They are saved locally and will resume next session, but it is safer to wait until syncing finishes.';
    return e.returnValue;
  }

  window.addEventListener('beforeunload', handler);

  installed = () => {
    clearInterval(pollInterval);
    window.removeEventListener('beforeunload', handler);
    installed = null;
  };

  return installed;
}
